import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fetchCurrencies, type Currency } from "@/lib/currencies/currenciesStore";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState } from "react";

type Props = {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
};

export function CurrencySelect({ value, onChange, disabled }: Props) {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchCurrencies()
      .then((rows) => {
        if (mounted) {
          setCurrencies(rows);
          if (value == null && rows.length > 0) {
            const pkr = rows.find((c) => c.code === "PKR") || rows[0];
            onChange(pkr.id);
          }
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const selected = currencies.find((c) => c.id === value);
  const displayLabel = loading
    ? "Loading currencies…"
    : selected
    ? `${selected.code} (${selected.symbol}) — ${selected.name}`
    : "Select currency";

  return (
    <div className="space-y-2">
      <Label className="text-xs text-muted-foreground">Company Base Currency</Label>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || loading}
            className={cn(
              "glass-input h-10 w-full justify-between font-normal text-sm px-3",
              !selected && "text-muted-foreground"
            )}
          >
            <span className="truncate">{displayLabel}</span>
            <ChevronsUpDown className="ml-1.5 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0 z-[100]" align="start" sideOffset={4}>
          <Command shouldFilter>
            <CommandInput placeholder="Search currency..." className="h-10 text-xs" />
            <CommandList className="max-h-[240px] overflow-y-auto">
              <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
                No currency found.
              </CommandEmpty>
              <CommandGroup>
                {currencies.map((c) => {
                  const isSelected = c.id === value;
                  return (
                    <CommandItem
                      key={c.id}
                      value={`${c.code} ${c.name} ${c.symbol}`}
                      onSelect={() => {
                        onChange(c.id);
                        setOpen(false);
                      }}
                      className="text-xs flex items-center justify-between cursor-pointer"
                    >
                      <div className="flex items-center gap-1.5 truncate min-w-0">
                        <span className="font-medium">{c.code}</span>
                        <span className="opacity-80">({c.symbol})</span>
                        <span className="opacity-75 truncate">— {c.name}</span>
                      </div>
                      <Check
                        className={cn(
                          "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
